"use client"

import { forwardRef, useRef, useEffect } from "react"
import type { ElementType } from "react"
import { useSidebarContext } from "./hooks/useSidebarContext"
import type { SidebarContentProps } from "./Sidebar.types"
import { SIDEBAR_TAILWIND_CLASSES, SIDEBAR_ARIA_ROLES } from "./Sidebar.constants"

/**
 * Componente de contenido del Sidebar (contenedor de items)
 *
 * @description
 * Contenedor que agrupa los items de navegación (SidebarItem).
 * Renderiza un `<div>` por defecto y aplica estilos de layout flex.
 *
 * Este componente:
 * - Registra su ref en el contexto para cálculos del indicador
 * - Aplica rol ARIA "list" para accesibilidad
 * - Centra y distribuye items verticalmente
 * - Permite customización completa con className y style
 *
 * Los items dentro de este contenedor son utilizados por:
 * - useSidebarIndicator: Para calcular posición del indicador
 * - useSidebarKeyboard: Para navegación por teclado
 *
 * @example
 * ```tsx
 * <SidebarNav>
 *   <SidebarContent>
 *     <SidebarItem href="/" icon={<HomeIcon />} />
 *     <SidebarItem href="/settings" icon={<SettingsIcon />} />
 *   </SidebarContent>
 * </SidebarNav>
 * ```
 *
 * @example
 * ```tsx
 * // Con estilos custom
 * <SidebarContent className="gap-2 py-4">
 *   <SidebarItem href="/" />
 * </SidebarContent>
 * ```
 *
 * @example
 * ```tsx
 * // Como lista semántica
 * <SidebarContent as="ul">
 *   <SidebarItem as="li" href="/" />
 * </SidebarContent>
 * ```
 */
export const SidebarContent = forwardRef<HTMLDivElement, SidebarContentProps>(function SidebarContent(
  { children, className = "", style, as, ...props },
  forwardedRef,
) {
  const { contentRef, debug, safeAreas } = useSidebarContext()

  // Elemento a renderizar (default: div)
  const Component = (as || "div") as ElementType

  // Ref local
  const localContentRef = useRef<HTMLDivElement>(null)

  // Calcular height dinámica basada en safe areas
  const contentHeight = 100 - safeAreas.topPercent - safeAreas.bottomPercent

  // Combinar refs en useEffect
  useEffect(() => {
    const node = localContentRef.current
    if (!node) return

    // Asignar a context ref
    if (contentRef && "current" in contentRef) {
      ;(contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    }

    // Asignar a forwarded ref
    if (typeof forwardedRef === "function") {
      forwardedRef(node)
    } else if (forwardedRef) {
      forwardedRef.current = node
    }
  }, [contentRef, forwardedRef])

  // Aplicar clases de debug mode
  const debugClasses = debug ? "bg-zinc-900/70 border-y-2 border-dashed border-emerald-400/60 relative" : ""

  return (
    <Component
      ref={localContentRef}
      className={`${SIDEBAR_TAILWIND_CLASSES.content} ${debugClasses} ${className}`}
      style={{
        height: `${contentHeight}%`,
        ...style,
      }}
      role={SIDEBAR_ARIA_ROLES.list}
      {...props}
    >
      {debug && (
        <span className="absolute top-1 right-1 text-[10px] text-emerald-300 font-mono z-50 bg-black/60 px-1.5 py-0.5 rounded">
          CONTENT {contentHeight}%
        </span>
      )}
      {children}
    </Component>
  )
})
